/**
 * IFC Asset Store
 *
 * Binary mesh payloads extracted from IFC files never belong in the document
 * JSON (a single chair can carry megabytes of vertex data). This side-store
 * parks each product's baked parts in IndexedDB keyed by a generated asset id
 * — the same externalization tradeoff assetSidecar.ts makes for raster data,
 * but typed-array native (structured clone, no base64 round trip). Families
 * and elements reference assets only through `{ kind: 'ifc', assetId }`.
 */

import { v4 as uuidv4 } from 'uuid';
import type { IfcMeshPartRecord } from './IfcParserService';

const DB_NAME = 'yap-ifc-assets';
const DB_VERSION = 1;
const STORE = 'assets';

export interface IfcAssetRecord {
    id: string;
    /** Product name at import time (display only). */
    productName: string;
    /** Original file label, e.g. "chair.ifc #24". */
    source: string;
    parts: IfcMeshPartRecord[];
    bbox: { min: [number, number, number]; max: [number, number, number] };
    /** Mesh axis convention at bake time: always IFC Z-up → app Z-up. */
    createdAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** Persist one baked product asset; returns its generated id. */
export async function storeIfcAsset(
    productName: string,
    source: string,
    parts: IfcMeshPartRecord[],
    bbox: IfcAssetRecord['bbox']
): Promise<string> {
    const id = uuidv4();
    const row: IfcAssetRecord = {
        id,
        productName,
        source,
        parts,
        bbox,
        createdAt: new Date().toISOString(),
    };
    const db = await openDatabase();
    await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(row);
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
    });
    return id;
}

/** Load a stored asset; null when the id is unknown (asset pruned). */
export async function loadIfcAsset(id: string): Promise<IfcAssetRecord | null> {
    try {
        const db = await openDatabase();
        return await new Promise<IfcAssetRecord | null>((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const request = tx.objectStore(STORE).get(id) as IDBRequest<IfcAssetRecord | undefined>;
            request.onsuccess = () => { db.close(); resolve(request.result ?? null); };
            request.onerror = () => { db.close(); reject(request.error); };
        });
    } catch {
        return null;
    }
}

/** Lightweight metadata (no vertex data) for library listings. */
export async function listIfcAssetSummaries(): Promise<Array<Pick<IfcAssetRecord, 'id' | 'productName' | 'source' | 'bbox'>>> {
    try {
        const db = await openDatabase();
        return await new Promise((resolve, reject) => {
            const tx = db.transaction(STORE, 'readonly');
            const request = tx.objectStore(STORE).getAll() as IDBRequest<IfcAssetRecord[]>;
            request.onsuccess = () => {
                db.close();
                resolve((request.result ?? []).map(({ id, productName, source, bbox }) => ({ id, productName, source, bbox })));
            };
            request.onerror = () => { db.close(); reject(request.error); };
        });
    } catch {
        return [];
    }
}
